import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const aggregateFinishedOutputsFromRequests = (requests) => {
  const aggregated = new Map();
  (requests || []).forEach((request) => {
    (request.items || []).forEach((item) => {
      const variantId = String(item.variant_id || '').trim();
      const qtyRequired = Number(item.shortfall ?? item.transfer_qty ?? item.required_qty ?? 0);
      if (!variantId || qtyRequired <= 0) return;
      const existing = aggregated.get(variantId) || {
        variant_id: variantId,
        qty_required: 0,
        note: '',
      };
      existing.qty_required += qtyRequired;
      aggregated.set(variantId, existing);
    });
  });
  return Array.from(aggregated.values());
};

export default function PackingRequestsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [packingRequests, setPackingRequests] = useState([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [packingStatus, setPackingStatus] = useState('pending');
  const [packingSearch, setPackingSearch] = useState('');
  const [packingDateFrom, setPackingDateFrom] = useState('');
  const [packingDateTo, setPackingDateTo] = useState('');

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const total = packingRequests.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [showPackingCompleteModal, setShowPackingCompleteModal] = useState(false);
  const [selectedPackingRequest, setSelectedPackingRequest] = useState(null);
  const [packingRemarks, setPackingRemarks] = useState('');
  const [rawItems, setRawItems] = useState([]);
  const [rawConsumptions, setRawConsumptions] = useState([{ raw_item_id: '', kg_required: '', note: '' }]);

  const rawItemOptions = useMemo(() => rawItems.filter((item) => item.is_active !== false), [rawItems]);

  const pagedRequests = useMemo(() => {
    if (!Array.isArray(packingRequests)) return [];
    const start = (page - 1) * pageSize;
    return packingRequests.slice(start, start + pageSize);
  }, [packingRequests, page]);

  const selectableRequests = useMemo(
    () => pagedRequests.filter((request) => String(request.status || '').toLowerCase() === 'pending'),
    [pagedRequests]
  );
  const selectedRequests = useMemo(
    () => packingRequests.filter((request) => selectedRequestIds.includes(request.id)),
    [packingRequests, selectedRequestIds]
  );
  const allSelectableOnPageSelected = selectableRequests.length > 0 && selectableRequests.every((request) => selectedRequestIds.includes(request.id));

  const fetchPackingRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (packingStatus && packingStatus !== 'all') params.status = packingStatus;
      if (packingSearch.trim()) params.search = packingSearch.trim();
      if (packingDateFrom) params.date_from = packingDateFrom;
      if (packingDateTo) params.date_to = packingDateTo;

      const response = await adminApi.getPackingRequests(params);
      const list = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setPackingRequests(Array.isArray(list) ? list : []);
      setSelectedRequestIds((prev) => prev.filter((id) => (Array.isArray(list) ? list : []).some((request) => request.id === id)));
      setPage(1);
    } catch (error) {
      toast.error('Failed to load packing requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackingRequests();
  }, [packingStatus, packingSearch, packingDateFrom, packingDateTo]);

  useEffect(() => {
    const fetchRawItems = async () => {
      try {
        const response = await adminApi.getRawItems();
        setRawItems(response.data || []);
      } catch (error) {
        // Optional lookup for modal; avoid noisy errors.
      }
    };
    fetchRawItems();
  }, []);

  const handleOpenPackingComplete = (request) => {
    setSelectedPackingRequest(request);
    setPackingRemarks('');
    setRawConsumptions([{ raw_item_id: '', kg_required: '', note: '' }]);
    setShowPackingCompleteModal(true);
  };

  const handleCompletePackingRequest = async () => {
    if (!selectedPackingRequest) return;

    const preparedRawConsumptions = rawConsumptions
      .filter((row) => row.raw_item_id && row.kg_required)
      .map((row) => ({
        raw_item_id: row.raw_item_id,
        kg_required: parseFloat(row.kg_required),
        note: row.note?.trim() || undefined,
      }))
      .filter((row) => Number.isFinite(row.kg_required) && row.kg_required > 0);

    setSaving(true);
    try {
      await adminApi.completePackingRequest(selectedPackingRequest.id, {
        remarks: packingRemarks,
        raw_consumptions: preparedRawConsumptions.length > 0 ? preparedRawConsumptions : undefined,
      });
      toast.success('Packing request completed');
      setShowPackingCompleteModal(false);
      setSelectedPackingRequest(null);
      setPackingRemarks('');
      setRawConsumptions([{ raw_item_id: '', kg_required: '', note: '' }]);
      fetchPackingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete packing request');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackingRequest = async (request) => {
    if (!request?.id) return;
    const confirmDelete = window.confirm('Delete this packing request? This cannot be undone.');
    if (!confirmDelete) return;

    setSaving(true);
    try {
      await adminApi.deletePackingRequest(request.id);
      toast.success('Packing request deleted');
      fetchPackingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete packing request');
    } finally {
      setSaving(false);
    }
  };

  const toggleRequestSelection = (requestId, checked) => {
    setSelectedRequestIds((prev) => {
      if (checked) {
        return prev.includes(requestId) ? prev : [...prev, requestId];
      }
      return prev.filter((id) => id !== requestId);
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    if (checked) {
      const pageIds = selectableRequests.map((request) => request.id);
      setSelectedRequestIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      return;
    }
    const pageIds = new Set(selectableRequests.map((request) => request.id));
    setSelectedRequestIds((prev) => prev.filter((id) => !pageIds.has(id)));
  };

  const handleCreatePackingSession = async () => {
    if (selectedRequests.length === 0) {
      toast.error('Select at least one pending packing request');
      return;
    }
    const plannedFinishedOutputs = aggregateFinishedOutputsFromRequests(selectedRequests);
    if (plannedFinishedOutputs.length === 0) {
      toast.error('Selected packing requests do not have any shortage items to plan');
      return;
    }

    setCreatingSession(true);
    try {
      const response = await adminApi.createPackingSession({
        session_name: selectedRequests.length === 1
          ? `${selectedRequests[0].order_ref || 'Packing'} Session`
          : `Packing Session - ${selectedRequests.length} Requests`,
        creation_mode: 'finished_first',
        packing_request_ids: selectedRequests.map((request) => request.id),
        assigned_staff: [],
        planned_raw_inputs: [],
        planned_packing_material_inputs: [],
        planned_finished_outputs: plannedFinishedOutputs,
        notes: '',
      });
      toast.success(`Packing session ${response.data?.session_ref || 'created'} created`);
      setSelectedRequestIds([]);
      await fetchPackingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create packing session');
    } finally {
      setCreatingSession(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="packing-requests-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Packing Requests</h1>
          <p className="text-[#A8A8A8] mt-1">Track orders that need packing due to stock shortages</p>
        </div>
        <Button
          onClick={handleCreatePackingSession}
          disabled={creatingSession || selectedRequestIds.length === 0}
          className="bg-[#C97A5E] hover:bg-[#B06850]"
        >
          {creatingSession ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Create Packing Session
        </Button>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={packingStatus} onValueChange={setPackingStatus}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <Input
                value={packingSearch}
                onChange={(e) => setPackingSearch(e.target.value)}
                placeholder="Order ref or customer"
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Date From</Label>
              <Input
                type="date"
                value={packingDateFrom}
                onChange={(e) => setPackingDateFrom(e.target.value)}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Date To</Label>
              <Input
                type="date"
                value={packingDateTo}
                onChange={(e) => setPackingDateTo(e.target.value)}
                className="border-[#E5E5E5]"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : packingRequests.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <AlertTriangle className="w-6 h-6 mr-2" />
              No packing requests
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">
                        <input
                          type="checkbox"
                          checked={allSelectableOnPageSelected}
                          onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                          disabled={selectableRequests.length === 0}
                          className="h-4 w-4 rounded border-[#CFCFCF]"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Order</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Shortage Items</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Expected Delivery</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {pagedRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-[#FAFAF9]/60 align-top">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedRequestIds.includes(request.id)}
                            onChange={(e) => toggleRequestSelection(request.id, e.target.checked)}
                            disabled={String(request.status || '').toLowerCase() !== 'pending'}
                            className="h-4 w-4 rounded border-[#CFCFCF]"
                          />
                        </td>
                        <td className="py-3 px-4 text-[#2D2420] whitespace-nowrap">
                          {formatDateTime(request.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-[#2D2420] font-medium">{request.order_ref || '-'}</div>
                          <div className="text-sm text-[#A8A8A8]">{request.order_id || ''}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-[#2D2420]">{request.customer_name || '-'}</div>
                          <div className="text-sm text-[#A8A8A8]">{request.customer_shop || ''}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-2">
                            {(request.items || []).map((item, idx) => (
                              <div key={`${request.id}-item-${idx}`} className="text-sm">
                                <div className="text-[#2D2420] font-medium">
                                  {item.product_name} {item.pack_size_label ? `(${item.pack_size_label})` : ''}
                                </div>
                                <div className="text-[#A8A8A8]">
                                  Required: {Math.round(item.required_qty || 0)} • Available: {Math.round(item.available_qty || 0)} • Short: {Math.round(item.shortfall || 0)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#2D2420] whitespace-nowrap">
                          {request.expected_delivery_date ? formatDateTime(request.expected_delivery_date) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            className={cn(
                              'text-xs',
                              request.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : request.status === 'in_progress'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-[#E76F51]/10 text-[#E76F51]'
                            )}
                          >
                            {request.status === 'completed' ? 'Completed' : request.status === 'in_progress' ? 'In Progress' : 'Pending'}
                          </Badge>
                          {request.packing_session_ref ? (
                            <div className="text-xs text-[#5C4033] mt-1">Session: {request.packing_session_ref}</div>
                          ) : null}
                          {request.status === 'completed' && request.remarks ? (
                            <div className="text-xs text-[#A8A8A8] mt-1">{request.remarks}</div>
                          ) : null}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex items-center gap-2">
                            {request.status === 'pending' ? (
                              <Button variant="outline" size="sm" onClick={() => handleOpenPackingComplete(request)}>
                                Complete
                              </Button>
                            ) : (
                              <span className="text-xs text-[#A8A8A8]">-</span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeletePackingRequest(request)}
                              disabled={saving}
                              title="Delete packing request"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                <p className="text-sm text-[#A8A8A8]">
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.max(1, prev - 1));
                        }}
                        className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-3 text-sm text-[#5C4033]">Page {page} of {totalPages}</span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((prev) => Math.min(totalPages, prev + 1));
                        }}
                        className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showPackingCompleteModal} onOpenChange={setShowPackingCompleteModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Complete Packing Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={packingRemarks}
                onChange={(e) => setPackingRemarks(e.target.value)}
                className="border-[#E5E5E5]"
                placeholder="Add completion remarks"
              />
            </div>

            <div className="border border-[#E5E5E5] rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2D2420]">Raw Material Consumption (Optional)</p>
                  <p className="text-xs text-[#A8A8A8]">Specify actual raw materials consumed for this packing run</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRawConsumptions((prev) => [...prev, { raw_item_id: '', kg_required: '', note: '' }])}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Row
                </Button>
              </div>

              {rawConsumptions.map((row, index) => (
                <div key={`packing-raw-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-5">
                    <Label>Raw Material</Label>
                    <Select
                      value={row.raw_item_id}
                      onValueChange={(value) => {
                        setRawConsumptions((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], raw_item_id: value };
                          return next;
                        });
                      }}
                    >
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select raw material" />
                      </SelectTrigger>
                      <SelectContent>
                        {rawItemOptions.map((item) => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label>KG Used</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={row.kg_required}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRawConsumptions((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], kg_required: value };
                          return next;
                        });
                      }}
                      className="border-[#E5E5E5]"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label>Note</Label>
                    <Input
                      value={row.note}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRawConsumptions((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], note: value };
                          return next;
                        });
                      }}
                      className="border-[#E5E5E5]"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setRawConsumptions((prev) => prev.filter((_, idx) => idx !== index))}
                      disabled={rawConsumptions.length === 1}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackingCompleteModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCompletePackingRequest} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
